import { useEffect } from "react";

/**
 * Lightweight SEO hook — updates <title>, <meta description>, <meta keywords>,
 * canonical + Open Graph tags based on the current route. No external deps.
 *
 * Usage:
 *   useSeo({
 *     title: "Pricing — Dr Trade Scalper Pro",
 *     description: "...",
 *     keywords: "bot MT5, tarif EA...",
 *     path: "/pricing",
 *   });
 */
const BASE = "https://drtradebots.com";

function upsertMeta(attr, key, value) {
    if (!value) return;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", value);
}

function upsertLink(rel, href, extra = {}) {
    if (!href) return;
    const selectorParts = [`link[rel="${rel}"]`];
    if (extra.hreflang) selectorParts.push(`[hreflang="${extra.hreflang}"]`);
    let el = document.head.querySelector(selectorParts.join(""));
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (extra.hreflang) el.setAttribute("hreflang", extra.hreflang);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

export function useSeo({ title, description, keywords, path = "/", ogType = "website", ogImage }) {
    useEffect(() => {
        if (title) document.title = title;
        if (description) {
            upsertMeta("name", "description", description);
            upsertMeta("property", "og:description", description);
            upsertMeta("name", "twitter:description", description);
        }
        if (keywords) upsertMeta("name", "keywords", keywords);
        if (title) {
            upsertMeta("property", "og:title", title);
            upsertMeta("name", "twitter:title", title);
        }
        upsertMeta("property", "og:type", ogType);
        const url = `${BASE}${path}`;
        upsertMeta("property", "og:url", url);
        upsertLink("canonical", url);
        upsertLink("alternate", `${BASE}${path}`, { hreflang: "fr" });
        upsertLink("alternate", `${BASE}${path}${path.includes("?") ? "&" : "?"}lang=en`, { hreflang: "en" });
        upsertLink("alternate", `${BASE}${path}${path.includes("?") ? "&" : "?"}lang=es`, { hreflang: "es" });
        upsertLink("alternate", `${BASE}${path}${path.includes("?") ? "&" : "?"}lang=de`, { hreflang: "de" });
        upsertLink("alternate", `${BASE}${path}`, { hreflang: "x-default" });
        if (ogImage) {
            upsertMeta("property", "og:image", ogImage);
            upsertMeta("name", "twitter:image", ogImage);
        }
    }, [title, description, keywords, path, ogType, ogImage]);
}

export default useSeo;
